import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const VoiceMessage = ({ audioUrl, duration, isOwn }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration || 0);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      // WICHTIG: Prüfen, ob die Duration gültig ist
      const d = audio.duration;
      if (d !== Infinity && !isNaN(d)) {
        setAudioDuration(d);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlayback = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    // Sicherstellen, dass wir nicht mit Infinity rechnen
    if (Number.isFinite(audioDuration) && audioDuration > 0) {
        audioRef.current.currentTime = percent * audioDuration;
    }
  };

  // ✅ FIX: Robuste Zeitformatierung
  const formatTime = (seconds) => {
    // Wenn seconds ungültig, Infinity oder negativ ist -> 0:00
    if (!Number.isFinite(seconds) || isNaN(seconds) || seconds < 0) return '0:00';
    
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Fortschrittsberechnung absichern
  const progress = (Number.isFinite(audioDuration) && audioDuration > 0) 
    ? (currentTime / audioDuration) * 100 
    : 0;

  return (
    <div className={`flex items-center gap-3 min-w-[200px] max-w-[280px] ${isOwn ? 'flex-row-reverse' : ''}`}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      
      <Button
        variant="ghost"
        size="icon"
        onClick={togglePlayback}
        className={`rounded-full shrink-0 ${
          isOwn 
            ? 'bg-white/20 hover:bg-white/30 text-white' 
            : 'bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500'
        }`}
        data-testid="voice-message-play-btn"
      >
        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </Button>

      <div className="flex-1 space-y-1">
        {/* Waveform / Progress bar */}
        <div 
          className="relative h-8 flex items-center cursor-pointer"
          onClick={handleSeek}
        >
          <div className="absolute inset-0 flex items-center gap-0.5">
            {[...Array(25)].map((_, i) => {
              const barProgress = (i / 25) * 100;
              const isActive = barProgress <= progress;
              return (
                <motion.div
                  key={i}
                  className={`w-1 rounded-full transition-colors ${
                    isActive 
                      ? (isOwn ? 'bg-white' : 'bg-emerald-500')
                      : (isOwn ? 'bg-white/40' : 'bg-slate-300 dark:bg-slate-500')
                  }`}
                  style={{ height: `${Math.random() * 60 + 40}%` }}
                  animate={isPlaying && isActive ? { opacity: [0.7, 1, 0.7] } : {}}
                  transition={{ repeat: Infinity, duration: 0.5 }}
                />
              );
            })}
          </div>
        </div>

        {/* Time display */}
        <div className={`text-xs ${isOwn ? 'text-white/70' : 'text-slate-500 dark:text-slate-400'}`}>
          {formatTime(isPlaying ? currentTime : audioDuration)}
        </div>
      </div>
    </div>
  );
};

export default VoiceMessage;